import { Driver, DriveStep } from "driver.js";
import { defaultTourConfig } from "..";
import { createCampaignGuideSteps } from "./create-campaign-guide";
import { dashboardGuideSteps } from "./dashboard-guide";
import { companyDetailsGuideSteps } from "./fill-company-profile-guide";
import { inviteAffiliateGuideSteps } from "./invite-affiliate-guide";
import { personalDetailsGuideSteps } from "./fill-personal-profile-guide";
import { programSettingsGuideSteps } from "./program-settings-guide";
import { appearanceSettingsGuideSteps } from "./fill-appearance-settings-guide";
import { shareResourcesGuideSteps } from "./share-resources-guide";
import { rewardsLandingGuideSteps } from "./rewards-landing-guide";

export function getCurrentPageLabel(): string {
  const pathname = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);
  const tab = searchParams.get("tab");

  switch (pathname) {
    case "/brand/campaigns":
      if (tab === "create") {
        return "A walkthrough on how to create a campaign";
      }
      return "About this page";
    case "/brand/settings":
      if (tab === "my-details") {
        return "A walkthrough on how to fill your personal information";
      }
      if (tab === "company-details") {
        return "A walkthrough on how to fill your company information";
      }
      if (tab === "appearance-settings") {
        return "A walkthrough on how to setup your Brand appearance in the eye of your affiliates";
      }
      if (tab === "program-settings") {
        return "A walkthrough on how to setup your program settings";
      }
      if (tab === "share-resources") {
        return "A walkthrough on how to share your resources with your affiliates";
      }
      return "About this page";
    case "/brand/dashboard":
      return "A walkthrough on how to navigate the dashboard";
    case "/brand/affiliates/invite":
      return "A walkthrough on how to invite an affiliate";
    case "/brand/rewards":
      return "A walkthrough on how to view your rewards";
    default:
      return "About this page";
  }
}

export function hasCurrentPageTour(): boolean {
  return getCurrentPageLabel() !== "About this page";
}

function getCurrentPageSteps(setCurrentPageLabel: (label: string) => void) {
  const pathname = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);
  const tab = searchParams.get("tab");

  // Set the label using the shared function
  setCurrentPageLabel(getCurrentPageLabel());

  switch (pathname) {
    case "/brand/campaigns":
      if (tab === "create") {
        return createCampaignGuideSteps;
      }
      return [];
    case "/brand/settings":
      if (tab === "my-details") {
        return personalDetailsGuideSteps;
      }
      if (tab === "company-details") {
        return companyDetailsGuideSteps;
      }
      if (tab === "appearance-settings") {
        return appearanceSettingsGuideSteps;
      }
      if (tab === "program-settings") {
        return programSettingsGuideSteps;
      }
      if (tab === "share-resources") {
        return shareResourcesGuideSteps;
      }
      return [];
    case "/brand/dashboard":
      return dashboardGuideSteps;
    case "/brand/affiliates/invite":
      return inviteAffiliateGuideSteps;
    case "/brand/rewards":
      return rewardsLandingGuideSteps;
    default:
      return [];
  }
}

const onHighlight = (driverObj: Driver) => (_: any, step: any) => {
  if (
    step.element === "#CCF-save-button" ||
    step.element === "#invite-affiliates-form-submit" ||
    step.element === "#PSF-save-program-button" ||
    step.element === "#PSF-save-payment-method-button" ||
    step.element === "#SPF-save-button" ||
    step.element === "#CD-save-company-profile" ||
    step.element === "#SR-submit-resource-button" ||
    step.element === "#AS-save-button"
  ) {
    setTimeout(() => {
      driverObj.destroy();
    }, 2000);
  }
};

const startCurrentPageTour = (
  tourDriver: Driver,
  setCurrentPageLabel: (label: string) => void
) => {
  const steps: DriveStep[] = getCurrentPageSteps(setCurrentPageLabel);

  tourDriver.setConfig(defaultTourConfig);
  // Here we set the unique steps for the current page tour
  tourDriver.setSteps(
    steps.map((step) => ({
      ...step,
      popover: {
        ...step.popover,
      },
      onHighlightStarted: onHighlight(tourDriver),
    }))
  );
  tourDriver.drive();
};

export default startCurrentPageTour;
